import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { LinearProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { FileUploadPhase } from "@/helper/customHooks/useFileUploadProgress";
import styles from "@/components/forms/inputs/fileUploadProgress.module.css";

interface Props {
  phase: FileUploadPhase;
  progressPercentage: number | null;
  uploadingTitle?: string;
  uploadingDescription?: string;
  processingTitle?: string;
  processingDescription?: string;
  dataTestId?: string;
  className?: string;
}

export default function FileUploadProgress({
  phase,
  progressPercentage,
  uploadingTitle,
  uploadingDescription,
  processingTitle,
  processingDescription,
  dataTestId = "file-upload-progress",
  className = "",
}: Props) {
  const { t } = useTranslation();

  if (phase === "idle") {
    return null;
  }

  const isProcessing = phase === "processing";
  const isDeterminate = !isProcessing && progressPercentage !== null;
  const title = isProcessing
    ? (processingTitle ?? t("file_upload_processing"))
    : (uploadingTitle ?? t("file_upload_in_progress"));
  const description = isProcessing
    ? (processingDescription ?? t("file_upload_processing_description"))
    : (uploadingDescription ?? t("file_upload_in_progress_description"));

  return (
    <section
      className={`${styles.progressCard} ${
        isProcessing ? styles.processingCard : ""
      } ${className}`}
      role="status"
      aria-live="polite"
      data-testid={dataTestId}
    >
      <div className={styles.progressHeader}>
        <span className={styles.iconContainer} aria-hidden="true">
          {isProcessing ? (
            <Inventory2OutlinedIcon />
          ) : (
            <CloudUploadOutlinedIcon />
          )}
        </span>
        <div className={styles.progressCopy}>
          <Typography className={styles.progressTitle}>{title}</Typography>
          <Typography className={styles.progressDescription}>
            {description}
          </Typography>
        </div>
        {progressPercentage !== null && (
          <span className={styles.percentageBadge}>
            {progressPercentage}%
          </span>
        )}
      </div>

      <LinearProgress
        className={styles.progressBar}
        variant={isDeterminate ? "determinate" : "indeterminate"}
        value={isDeterminate ? progressPercentage : undefined}
        aria-label={title}
        sx={{
          "& .MuiLinearProgress-bar": {
            borderRadius: "999px",
            background:
              "linear-gradient(90deg, #fa6522 0%, #b846c8 52%, #8c1ec8 100%)",
          },
        }}
      />
    </section>
  );
}
